import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { PageHeader } from "./fordon";
import { HubEditorial, faqSchema } from "@/components/HubEditorial";
import { forsakringFaq, forsakringSections } from "@/data/nationella-guider-2";

const URL = "https://www.akhusbilfritid.se/husbilsforsakring";

export const Route = createFileRoute("/husbilsforsakring")({
  // FAQ-schemat byggs i loadern så guidens textdata hamnar i sidans egen
  // chunk i stället för i initial-bundlen.
  codeSplitGroupings: [["loader", "component"]],
  loader: () => ({ faqLd: faqSchema(forsakringFaq) }),
  head: ({ loaderData }) => ({
    meta: [
      { title: "Husbilsförsäkring 2026 – pris, halv eller hel & villkor" },
      {
        name: "description",
        content:
          "Vad husbilsförsäkring kostar per år, när helförsäkring lönar sig och vilka villkor som avgör vid skada – fukt, förvaring och lösöre.",
      },
      {
        name: "keywords",
        content:
          "husbilsförsäkring, husbilsförsäkring pris, helförsäkring husbil, halvförsäkring husbil, vagnskadegaranti husbil, avställning husbil försäkring",
      },
      { property: "og:type", content: "article" },
      { property: "og:title", content: "Husbilsförsäkring 2026 – pris, halv eller hel & villkoren" },
      {
        property: "og:description",
        content:
          "Premienivåer, självrisker och de fyra villkorsrader som avgör om skadan ersätts. Från vår verkstad i Kalix.",
      },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "@id": `${URL}#article`,
          headline: "Husbilsförsäkring – pris, skyddsnivåer och villkoren som gäller",
          url: URL,
          inLanguage: "sv-SE",
          about: "Försäkring av husbil i Sverige",
          author: { "@type": "Organization", name: "AK Husbil & Fritid" },
          publisher: { "@type": "Organization", name: "AK Husbil & Fritid" },
        }),
      },
      ...(loaderData ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.faqLd) }] : []),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
            { "@type": "ListItem", position: 2, name: "Guider", item: "https://www.akhusbilfritid.se/guider" },
            { "@type": "ListItem", position: 3, name: "Husbilsförsäkring", item: URL },
          ],
        }),
      },
    ],
  }),
  component: ForsakringPage,
});

const facts = [
  { k: "2 500–11 000 kr", v: "årspremie beroende på värde och skyddsnivå" },
  { k: "6 000–10 000 kr", v: "typisk självrisk vid vagnskada" },
  { k: "30–40 %", v: "lägre årskostnad med avställning nov–mars" },
  { k: "150 000 kr", v: "gräns där helförsäkring nästan alltid lönar sig" },
];

function ForsakringPage() {
  return (
    <>
      <PageHeader
        label="Nationell guide · Försäkring"
        title={
          <>
            Husbilsförsäkring — <span className="font-italic-display text-accent">villkoren</span> som avgör.
          </>
        }
        sub="Vad premien faktiskt kostar, när helförsäkring lönar sig och vilka fyra rader i villkoren som bestämmer om skadan ersätts. Siffrorna kommer från kunders offerter och våra egna skadeärenden."
      />

      <section className="container-edge pt-14 md:pt-20">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {facts.map((f) => (
            <div key={f.k} className="rounded-3xl border border-border bg-card p-6">
              <p className="font-display text-3xl leading-none text-accent">{f.k}</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.v}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="pt-14 md:pt-20">
        <HubEditorial eyebrow="Guiden" sections={forsakringSections} faq={forsakringFaq} />
      </div>

      <section className="container-edge pb-20 md:pb-24">
        <div className="rounded-3xl border border-border bg-warm p-7 md:p-10">
          <p className="eyebrow">Läs vidare</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <Link
              to="/fukttest-husbil"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Fukttest — kontrollen försäkringen kräver</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/verktyg/manadskostnad-husbil"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Räkna din totala månadskostnad</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/besiktning-husbil"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Besiktning av husbil — intervall och fällor</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
